import { useState, useEffect, useTransition, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";

interface SupplyItem {
  id: string;
  name: string;
  description: string;
  price: number;
  sellingPrice: number;
  returnRate: number;
  raised: number;
  target: number;
  stockQuantity: number;
  type: "trial" | "vip";
  imageUrl?: string;
}

const CACHE_KEY = "home_supply_cache_v1";
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// ---- Per-tab filtered cache ----
function getTabFilteredCacheKey(tab: "trial" | "vip"): string {
  return `home_filtered_${tab}_v1`;
}

function getTabFilteredCached(tab: "trial" | "vip"): SupplyItem[] | null {
  try {
    const raw = sessionStorage.getItem(getTabFilteredCacheKey(tab));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.data || !parsed.timestamp) return null;
    if (Date.now() - parsed.timestamp > CACHE_TTL) {
      sessionStorage.removeItem(getTabFilteredCacheKey(tab));
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function setTabFilteredCached(tab: "trial" | "vip", data: SupplyItem[]) {
  try {
    sessionStorage.setItem(
      getTabFilteredCacheKey(tab),
      JSON.stringify({ data, timestamp: Date.now() }),
    );
  } catch {
    // ignore
  }
}

function getCached(): { data: SupplyItem[]; timestamp: number } | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.data || !parsed.timestamp) return null;
    if (Date.now() - parsed.timestamp > CACHE_TTL) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function setCached(data: SupplyItem[]) {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ data, timestamp: Date.now() }),
    );
  } catch {
    // ignore
  }
}

function formatItem(item: Record<string, unknown>): SupplyItem {
  const price = item.price as number;
  const sellingPrice = (item.selling_price as number) ?? Math.round(price * (1 + Number((item.return_rate as number) || 25) / 100));
  const calculatedReturnRate = price > 0 ? ((sellingPrice - price) / price) * 100 : 0;
  return {
    id: item.id as string,
    name: item.name as string,
    description: (item.description as string) || "",
    price,
    sellingPrice,
    returnRate: calculatedReturnRate,
    raised: item.raised as number,
    target: item.target as number,
    stockQuantity: (item.stock_quantity as number) || 0,
    type: item.type as "trial" | "vip",
    imageUrl: (item.image_url as string) || undefined,
  };
}

function SupplyCard({ product }: { product: SupplyItem }) {
  const [imgError, setImgError] = useState(false);

  const progress =
    product.stockQuantity > 0
      ? Math.min(
          100,
          Math.round((product.raised / product.stockQuantity) * 100),
        )
      : 0;

  const progressColor =
    progress >= 80
      ? "bg-emerald-500"
      : progress >= 50
        ? "bg-amber-500"
        : "bg-rose-500";

  const profit = product.sellingPrice - product.price;
  const remaining = Math.max(0, product.stockQuantity - product.raised);

  return (
    <div className="group relative bg-white rounded-2xl border border-stone-200 overflow-hidden hover:border-stone-300 transition-all">
      {/* Image area */}
      <div className="relative w-full h-48 md:h-52 bg-stone-100 flex items-center justify-center overflow-hidden">
        {product.imageUrl && !imgError ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                product.type === "trial" ? "bg-rose-100" : "bg-amber-100"
              }`}
            >
              <i
                className={`${
                  product.type === "trial"
                    ? "ri-sparkling-line text-rose-600"
                    : "ri-vip-crown-line text-amber-600"
                } text-xl`}
              />
            </div>
            <span className="font-body text-xs text-stone-400 text-center px-4">
              {product.name}
            </span>
          </div>
        )}

        {/* Zone tag */}
        <div className="absolute top-3 left-3">
          {product.type === "trial" ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-rose-800 text-white font-body text-[10px] font-semibold">
              <i className="ri-sparkling-line" />
              體驗
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500 text-stone-900 font-body text-[10px] font-semibold">
              <i className="ri-vip-crown-line" />
              VIP
            </span>
          )}
        </div>

        {/* Progress badge */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 rounded-full bg-white/20 overflow-hidden">
              <div
                className={`h-full rounded-full ${progressColor} transition-all duration-700`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="font-body text-[10px] text-white font-semibold">
              {progress}%
            </span>
          </div>
        </div>
      </div>

      <div className="p-4">
        <h3 className="font-body text-sm font-semibold text-stone-800 mb-1 line-clamp-1">
          {product.name}
        </h3>
        <p className="font-body text-[11px] text-stone-500 mb-3 line-clamp-1">
          {product.description}
        </p>

        <div className="flex items-center gap-3 mb-3">
          <div>
            <p className="font-body text-[10px] text-stone-400">認購價</p>
            <p className="font-display text-sm font-bold text-stone-800">
              NT$ {product.price.toLocaleString()}
            </p>
          </div>
          <div className="w-px h-8 bg-stone-200" />
          <div>
            <p className="font-body text-[10px] text-stone-400">銷售價</p>
            <p className="font-display text-sm font-bold text-emerald-600">
              NT$ {product.sellingPrice.toLocaleString()}
            </p>
          </div>
          <div className="ml-auto">
            <span className="font-body text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
              NT$ {profit.toLocaleString()}
            </span>
          </div>
        </div>

        {product.type === "trial" ? (
          <p className="font-body text-[10px] text-stone-500 mb-3">
            限購 {remaining} 組
          </p>
        ) : (
          <p className="font-body text-[10px] text-stone-500 mb-3">
            必購 {remaining} 組
          </p>
        )}

        <Link
          to={
            product.type === "trial"
              ? "/subscribe-trial"
              : "/vip-subscribe"
          }
          className="block w-full text-center font-body text-xs font-semibold py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800 transition-colors whitespace-nowrap"
        >
          立即認購
        </Link>
      </div>
    </div>
  );
}

export default function HomeSupplyPreview() {
  const [activeTab, setActiveTab] = useState<"trial" | "vip">("trial");
  const [supplyItems, setSupplyItems] = useState<SupplyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPending, startTransition] = useTransition();

  // Debounce ref for tab switching
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- Fetch all products on mount ----
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("products")
        .select("id, name, description, price, selling_price, return_rate, raised, target, stock_quantity, type, image_url, created_at")
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(100);

      if (data && !error) {
        const formatted = data.map(formatItem);
        setSupplyItems(formatted);
        setCached(formatted);
        // Also cache per-tab filtered results
        const trialItems = formatted.filter((p) => p.type === "trial").slice(0, 3);
        const vipItems = formatted.filter((p) => p.type === "vip").slice(0, 3);
        setTabFilteredCached("trial", trialItems);
        setTabFilteredCached("vip", vipItems);
      }
      setLoading(false);
    };

    const cached = getCached();
    if (cached) {
      setSupplyItems(cached.data);
      setLoading(false);
      // Pre-populate per-tab cache from full cache
      const trialItems = cached.data.filter((p) => p.type === "trial").slice(0, 3);
      const vipItems = cached.data.filter((p) => p.type === "vip").slice(0, 3);
      setTabFilteredCached("trial", trialItems);
      setTabFilteredCached("vip", vipItems);
      // Background silent refresh
      supabase
        .from("products")
        .select("id, name, description, price, selling_price, return_rate, raised, target, stock_quantity, type, image_url, created_at")
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(100)
        .then(({ data, error }) => {
          if (error) {
            console.warn("[HomeSupplyPreview] background refresh failed:", error);
            return;
          }
          const formatted = (data || []).map(formatItem);
          setSupplyItems(formatted);
          setCached(formatted);
          const trialItems = formatted.filter((p) => p.type === "trial").slice(0, 3);
          const vipItems = formatted.filter((p) => p.type === "vip").slice(0, 3);
          setTabFilteredCached("trial", trialItems);
          setTabFilteredCached("vip", vipItems);
        });
    } else {
      fetchProducts();
    }
  }, []);

  // ---- Tab switch: debounced + transitioned ----
  const handleTabChange = useCallback(
    (tab: "trial" | "vip") => {
      if (tab === activeTab) return;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        startTransition(() => {
          setActiveTab(tab);
        });
      }, 80);
    },
    [activeTab, startTransition],
  );

  // ---- Filtered display with cache fallback ----
  const filtered = useMemo(() => {
    // If supplyItems hasn't loaded, try per-tab cache first
    if (supplyItems.length === 0) {
      const cached = getTabFilteredCached(activeTab);
      return cached || [];
    }
    const result = supplyItems
      .filter((item) => item.type === activeTab)
      .slice(0, 3);
    // Cache the result
    if (result.length > 0) {
      setTabFilteredCached(activeTab, result);
    }
    return result;
  }, [supplyItems, activeTab]);

  // Skeleton only when truly no data available
  const showSkeleton = loading && supplyItems.length === 0 && filtered.length === 0;

  return (
    <section className="w-full bg-white py-12 md:py-16">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 md:mb-10">
          <div>
            <h2 className="font-display text-xl md:text-2xl font-bold text-stone-800">
              精選貨源
            </h2>
            <p className="font-body text-sm text-stone-500 mt-1">
              瀏覽體驗區與 VIP 區熱門認購商品，把握獲利機會
            </p>
          </div>
          <Link
            to="/shop"
            className="inline-flex items-center gap-1.5 font-body text-sm font-medium text-rose-700 hover:text-rose-900 transition-colors whitespace-nowrap"
          >
            查看全部貨源
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 p-1 bg-stone-100 rounded-full w-fit mb-6">
          <button
            onClick={() => handleTabChange("trial")}
            className={`font-body text-xs font-medium px-4 py-2 rounded-full transition-all whitespace-nowrap ${
              activeTab === "trial"
                ? "bg-rose-800 text-white"
                : "text-stone-600 hover:text-stone-800"
            } ${isPending && activeTab !== "trial" ? "opacity-60" : ""}`}
          >
            <i className="ri-sparkling-line mr-1" />
            體驗認購區
          </button>
          <button
            onClick={() => handleTabChange("vip")}
            className={`font-body text-xs font-medium px-4 py-2 rounded-full transition-all whitespace-nowrap ${
              activeTab === "vip"
                ? "bg-amber-500 text-stone-900"
                : "text-stone-600 hover:text-stone-800"
            } ${isPending && activeTab !== "vip" ? "opacity-60" : ""}`}
          >
            <i className="ri-vip-crown-line mr-1" />
            VIP認購區
          </button>
        </div>

        {/* Cards — Stale-While-Revalidate */}
        {showSkeleton ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-stone-200 overflow-hidden"
              >
                <div className="w-full h-48 md:h-52 bg-stone-100 animate-pulse" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-stone-100 rounded animate-pulse w-3/4" />
                  <div className="h-3 bg-stone-100 rounded animate-pulse w-1/2" />
                  <div className="h-8 bg-stone-100 rounded animate-pulse w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 transition-opacity duration-300 ${
              isPending ? "opacity-60" : "opacity-100"
            }`}
          >
            {filtered.map((product) => (
              <SupplyCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}